import { InsuranceType } from 'domains/signup/data/types';
import { NOT_ACCEPTED_INSURANCE_PROVIDER_OPTION } from 'domains/signup/insurance/data/insurance';
import { MachineStateType } from 'utils/xstate/types';
import { assign, setup } from 'xstate';

export type CompletedInsuranceMachineData = {
  state: string;
  useInsurance: boolean;
  insuranceType: InsuranceType;
  medicaidProvider: string;
};

export type InsuranceResult = {
  data: CompletedInsuranceMachineData;
  earlyExit?: boolean;
};

// Machine's input
export type InsuranceMachineInput = {
  prevState?: Partial<CompletedInsuranceMachineData>;
  canExitByBack?: boolean;
};

type InternalContext = {
  data: Partial<CompletedInsuranceMachineData>;
  earlyExit?: boolean;
} & Pick<InsuranceMachineInput, 'canExitByBack'>;

/** @xstate-layout N4IgpgJg5mDOIC5QEsB2sCuAnAhqgxmALI74AWaYAdGKgC5hYCqsYAkutnoQMSYBGAW2R0W7TrgJgA2gAYAuolAAHAPawRyVaiUgAHogC0AJgCMADiqnTAdmOyALADZZxgMyzHbgDQgAnkYArDZUDqZOTjYOssFODsZxAL6JvmiYkoQk5JQ09IxiHOncYHwYQiIFEsXSpopIIGoadFo69QYINlFUbjaBstYOg7IAnKbDvgEIhsGh4ZHRsfFJKSBpXFJZFKjUtAxYheuEACp+yiUCwnQHGWAnZ3J1Kuqa2rrtJmNUgcM9Dh7DwwcgVMgwmiB6xio5lMfXMw3M5g8bnCgWSqSqG1IWx2eX2GOOp3OZUu12KdxktV0jRerVA7TcM0CTmMgUCbgWNmZsicYIQEKhMNkcIRSNsaNW+OIWJyu0YpKk5J4-FIAGsHlTns1Xm0jECQoFnIjzGF3KMnIFeYZ2VQnMN+uzHPZzJzTOK1jdNjLcURIMh8DhkBAAApYVQAN0DjFK5ToPogfoDwdDEYgjHV9WpWtp+iMCX1P057hhMKcPn8iHMIxtQPsNnZwycplcbslnu2uT2cYTgZD4cjWGjly7-p7yf7NUeDU1LTeRnNIVG-VZ5mMxhs-V50Mhjlt9iLw2+5hbRUx2Xbsqww8TvZTUeV+DVCg1TRnOqmeq+hrcxtMppRvLcRsqBsM0okbNwDwSBxj0OKUzxxTtfRHJM+1TAdBCQ68xzQgBRLBQywdMnhfbU6UQcJhm6b5jAbEUojrMtJm+BwvgZCDKxZO1Ihgj1pXbd1igAOVUK5UEEsA6AAd1ULAVT4chIAwAAbMAAHURDIENkDDHAGCDHA-CIqcSOzd5vkCKgYnMb5SzCb84V5awDUs6xbVMZEQQSI8VgE09sQ7fJWHlXh70fSdM1fMipmGGxTFY2060cdzYtMRyPK+TkHARA1BkCBJkhWVBVFTeB6l8zI+LAZ8aVnKYm0+NkEo8OyUstetLLCWKek6WRencnjijbBDAvEE9CGqrNasMdzIQBOb5vmy0Ejig05jraFhlXaCfNbSqArxMbbkJCbIpzOrANCTlgl-CIIk8GxHMBWZjS5Rx1ybbb0UOob9qvUdUMYE7SLO6bnGArdGybfpzEbXlGxCZaD0BCJ2WWL7YJ+8qwAAYVUQRlBUhggezOhVGUciYUhF113rX9rF8FSADM6CMcIWLMLK-l6TweZ5EB+BE0nBHI+xZGA+HZFpsxUpALBkCgMgWffdmQWNHo+h57lfEkwM6DIEXZF8MgwHlxXECy3x3jZqgObV7nNb5yYnKpiWpesAa-JyLHhNE8SpJklViam74xbAkZGxcY1HP6SFjEGBxaMBOt4gKxIgA */
export const insuranceMachineV2 = setup({
  types: {
    context: {} as InternalContext,
    events: {} as
      | { type: 'back' }
      | { type: 'submitUseInsurance'; useInsurance: boolean }
      | { type: 'submitInsuranceType'; insuranceType: InsuranceType }
      | { type: 'submitMedicaidProvider'; medicaidProvider: string }
      | { type: 'scheduleWithPrivatePay' }
      | { type: 'medicaidProviderError' },

    input: {} as InsuranceMachineInput,
    output: {} as InsuranceResult,
  },

  actions: {
    saveUseInsurance: assign(({ context }, params: { useInsurance: boolean }) => ({
      data: { ...context.data, useInsurance: params.useInsurance },
    })),
    saveInsuranceType: assign(({ context }, params: { insuranceType: InsuranceType }) => ({
      data: { ...context.data, insuranceType: params.insuranceType },
    })),
    saveMedicaidProvider: assign(({ context }, params: { medicaidProvider: string }) => ({
      data: { ...context.data, medicaidProvider: params.medicaidProvider },
    })),
    disableUseInsurance: assign(({ context }) => ({
      data: { ...context.data, useInsurance: false },
    })),
  },
  guards: {
    isUsingInsurance: (_, params: { useInsurance: boolean }) => params.useInsurance,
    isUsingMedicaid: (_, params: { insuranceType: InsuranceType }) => params.insuranceType === 'medicaid',
    medicaidProviderIsInNetwork: (_, params: { medicaidProvider: string }) =>
      params.medicaidProvider !== NOT_ACCEPTED_INSURANCE_PROVIDER_OPTION.value,

    canExitEarly: ({ context }) => context.canExitByBack === true,
  },
}).createMachine({
  id: 'insuranceMachine',
  initial: 'enterUseInsurance',

  context: ({ input: { prevState, canExitByBack } }) => ({ data: { ...prevState }, canExitByBack }),

  states: {
    enterUseInsurance: {
      on: {
        submitUseInsurance: [
          {
            target: 'enterInsuranceType',
            guard: {
              type: 'isUsingInsurance',
              params: ({ event }) => event,
            },

            actions: {
              type: 'saveUseInsurance',
              params: ({ event }) => event,
            },
          },
          {
            target: 'insuranceComplete',
            actions: {
              type: 'saveUseInsurance',
              params: ({ event }) => event,
            },
          },
        ],

        back: { target: 'insuranceComplete', actions: assign({ earlyExit: true }), guard: 'canExitEarly' },
      },
    },

    enterInsuranceType: {
      on: {
        submitInsuranceType: [
          {
            target: 'enterMedicaidProvider',
            guard: {
              type: 'isUsingMedicaid',
              params: ({ event }) => event,
            },
            actions: {
              type: 'saveInsuranceType',
              params: ({ event }) => event,
            },
          },
          {
            target: 'insuranceComplete',
            actions: {
              type: 'saveInsuranceType',
              params: ({ event }) => event,
            },
          },
        ],

        back: 'enterUseInsurance',
      },
    },

    enterMedicaidProvider: {
      on: {
        submitMedicaidProvider: [
          {
            target: 'insuranceComplete',
            guard: {
              type: 'medicaidProviderIsInNetwork',
              params: ({ event }) => event,
            },

            actions: {
              type: 'saveMedicaidProvider',
              params: ({ event }) => event,
            },
          },
          {
            target: 'insuranceNotInNetwork',
            actions: [
              {
                type: 'saveMedicaidProvider',
                params: ({ event }) => event,
              },
              { type: 'disableUseInsurance' },
            ],
          },
        ],

        back: 'enterInsuranceType',
        medicaidProviderError: {
          target: 'insuranceComplete',
          actions: 'disableUseInsurance',
        },
      },
    },

    insuranceComplete: {
      type: 'final',
    },

    insuranceNotInNetwork: {
      on: {
        scheduleWithPrivatePay: 'insuranceComplete',
      },
    },
  },

  output: ({ context }) => {
    const { insuranceType, medicaidProvider, useInsurance, state } = context.data as CompletedInsuranceMachineData;
    const result: InsuranceResult = {
      data: { insuranceType, medicaidProvider, useInsurance, state },
      earlyExit: context.earlyExit,
    };

    return result;
  },
});

export type InsuranceEntryState = MachineStateType<typeof insuranceMachineV2>;
